import axios from 'axios';

// Get the API URL from Vite environment variables. 
const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const axiosInstance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
    transformResponse: [function (data) {
        if (typeof data === 'string') {
            // Eliminar scripts inyectados por el hosting gratuito/compartido
            const cleanedData = data.replace(/<script[^>]*>.*?<\/script>/gi, '').trim();
            try {
                return JSON.parse(cleanedData);
            } catch (e) {
                return cleanedData;
            }
        }
        return data;
    }],
});

// Request interceptor to attach the Bearer token
axiosInstance.interceptors.request.use(
    (config) => {
        // Obtenemos el token y nos aseguramos de que no sea una cadena de texto "null" o "undefined"
        let token = localStorage.getItem('auth_token') || localStorage.getItem('token');

        if (token && token !== 'null' && token !== 'undefined') {
            config.headers.Authorization = `Bearer ${token}`;
            // Enviar cabecera duplicada para saltar filtros de OpenLiteSpeed/CGI
            config.headers['X-Tornado-Auth'] = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor para manejar errores 401 (Sesión expirada o inválida)
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Manejar tanto 401 (Unauthorized) como 419 (Authentication Timeout/CSRF Mismatch)
        if (error.response && (error.response.status === 401 || error.response.status === 419)) {
            const isLoginRequest = error.config.url.includes('/login');
            const isCsrfRequest = error.config.url.includes('/csrf-cookie');

            // DIAGNÓSTICO: Guardar detalles del error para depuración
            const errorData = {
                status: error.response.status,
                url: error.config.url,
                method: error.config.method,
                time: new Date().toISOString(),
                data: error.response.data
            };
            sessionStorage.setItem('last_auth_error', JSON.stringify(errorData));
            console.error("DEBUG AUTH ERROR:", errorData);

            // Si falla el login o  la obtención del CSRF, no redirigimos (el componente Login manejará el error)
            if (isLoginRequest || isCsrfRequest) {
                return Promise.reject(error);
            }

            // AUTO-REINTENTO PARA ERROR 419 (CSRF):
            // Si es un error 419, intentamos refrescar el token CSRF una vez antes de rendirnos
            if (error.response.status === 419 && !error.config._retry) {
                error.config._retry = true;
                console.warn("Error 419 detectado . Intentando refrescar CSRF...");
                try {
                    await axios.get(`${baseURL}/sanctum/csrf-cookie`, { withCredentials: true });
                    return axiosInstance(error.config); // Reintentar la petición original
                } catch (csrfError) {
                    console.error("Fallo al refrescar CSRF . Forzando logout.");
                }
            }

            // Solo limpiamos y redirigimos si no estamos ya en el login
            if (!window.location.pathname.includes('/login')) {
                const token = localStorage.getItem('auth_token');

                if (token && error.response.status === 401) {
                    console.warn("401 detectado con token presente. Posible fallo de cabeceras en OLS. Reintentando en 2s...");
                }

                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');

                // Limpiar cookies de sesión
                document.cookie = "laravel_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                document.cookie = "XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

                setTimeout(() => {
                    // Verificación final: Solo redirigimos si seguimos sin estar en el login
                    if (!window.location.pathname.includes('/login')) {
                        window.location.href = '/login?expired=1';
                    }
                }, 1500);
            }
        }

        // Si es un error 404, es un problema de configuración de OpenLiteSpeed/Rutas, NO de sesión.
        // NUNCA debemos redirigir al login por un 404.
        if (error.response && error.response.status === 404) {
            console.error("ERROR 404: La ruta no existe en el servidor. Revisa las Rewrite Rules de OLS.", error.config.url);
            return Promise.reject(error);
        }

        // Si es un error de red o de servidor (500+), NO borramos nada.
        if (!error.response || (error.response.status >= 500)) {
            console.error(`Fallo de conexión o error de servidor (${error.response?.status || 'Network Error'}). Manteniendo estado.`);
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
